import React, { useState, useEffect, useCallback } from 'react';
import { Card, Select, Row, Col, Typography, Space, Empty, Avatar, Spin, message, Button, Input, Badge } from 'antd';
import { UserOutlined, CalendarOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchProfilePublications } from '../server/publications/publicationServer';
import EventCard from './EventCard';

const { Option } = Select;
const { Title, Text } = Typography;

const ProfileEventsViewer = ({ profiles, profilesLoading, searchProfiles }) => {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [filteredProfiles, setFilteredProfiles] = useState(profiles || []);
  const [leftSearchTimeout, setLeftSearchTimeout] = useState(null);
  const [leftSearchLoading, setLeftSearchLoading] = useState(false);

  // Update search results when initial profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0) {
      setSearchResults(profiles);
      if (!searchValue) {
        setFilteredProfiles(profiles);
      }
    }
  }, [profiles]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (leftSearchTimeout) {
        clearTimeout(leftSearchTimeout);
      }
    };
  }, [searchTimeout, leftSearchTimeout]);

  // Debounced search function
  const debouncedSearch = useCallback(async (searchTerm) => {
    if (!searchProfiles) return;
    
    setSearchLoading(true);
    try {
      const results = await searchProfiles(searchTerm);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching profiles:', error);
      message.error('Failed to search profiles');
    } finally {
      setSearchLoading(false);
    }
  }, [searchProfiles]);

  const handleProfileSearch = (value) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If empty search, show initial profiles
    if (!value || value.trim() === '') {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }
    
    // Set loading state immediately
    setSearchLoading(true);
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      debouncedSearch(value.trim());
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
  };

  // Debounced MeiliSearch for left search box
  const debouncedLeftSearch = useCallback(async (searchTerm) => {
    if (!searchProfiles) return;
    
    setLeftSearchLoading(true);
    try {
      const results = await searchProfiles(searchTerm, { limit: 100 });
      setFilteredProfiles(results);
    } catch (error) {
      console.error('Error searching profiles:', error);
      message.error('Failed to search profiles');
      setFilteredProfiles([]);
    } finally {
      setLeftSearchLoading(false);
    }
  }, [searchProfiles]);

  // Handle left search box with MeiliSearch
  const handleLocalSearch = (value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (leftSearchTimeout) {
      clearTimeout(leftSearchTimeout);
    }
    
    // If empty search, show initial profiles
    if (!value || value.trim() === '') {
      setFilteredProfiles(profiles || []);
      setLeftSearchLoading(false);
      return;
    }
    
    // Set loading state immediately
    setLeftSearchLoading(true);
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      debouncedLeftSearch(value.trim());
    }, 300); // 300ms debounce
    
    setLeftSearchTimeout(timeout);
  };

  const handleProfileSelect = async (profileId) => {
    const selected = searchResults.find(p => p._id === profileId);
    setSelectedProfile(selected);
    
    if (selected) {
      await fetchProfileEvents(profileId);
    }
  };

  const fetchProfileEvents = async (profileId) => {
    try {
      setEventsLoading(true);
      console.log('🎫 [ProfileEventsViewer] Fetching events for profile:', profileId);
      
      const response = await fetchProfilePublications(profileId, {
        publicationType: 'Event',
        optimized: true,
        pagination: false
      });
      
      console.log('🎫 [ProfileEventsViewer] Events response:', response);
      const allEvents = response?.data || [];
      
      // Filter to only show upcoming events (start date later than now)
      const now = new Date();
      const upcomingEvents = allEvents.filter(event => {
        const eventData = event.publicationData || event;
        const startDate = new Date(eventData.startTime);
        return startDate > now;
      });
      
      setEvents(upcomingEvents);
      
      message.success(`Found ${upcomingEvents.length} upcoming events for this profile`);
    } catch (error) {
      console.error('🎫 [ProfileEventsViewer] Error fetching profile events:', error);
      message.error('Failed to fetch events for this profile');
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedProfile(null);
    setEvents([]);
  };

  // Since we're only showing upcoming events, all events in the array are upcoming
  // Sort events by start time (earliest upcoming events first)
  const sortedEvents = [...events].sort((a, b) => {
    const eventA = a.publicationData || a;
    const eventB = b.publicationData || b;
    const dateA = new Date(eventA.startTime);
    const dateB = new Date(eventB.startTime);
    return dateA - dateB; // Earliest first
  });

  return (
    <Card 
      title={
        <Space>
          <UserOutlined />
          Profile Event Comparison
        </Space>
      } 
      className="mb-4"
    >
      <Row gutter={[16, 16]}>
        {/* Profile Browser Section */}
        <Col span={24}>
          <Card size="small" title="Browse Existing Profiles" className="mb-4">
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Search Existing Profiles ({leftSearchLoading ? 'searching...' : `${filteredProfiles.length} found`}):</Text>
                <Input
                  placeholder="Search profiles by name, location, etc..."
                  prefix={leftSearchLoading ? <Spin size="small" /> : <SearchOutlined />}
                  value={searchValue}
                  onChange={(e) => handleLocalSearch(e.target.value)}
                  allowClear
                  className="mt-2"
                  disabled={leftSearchLoading}
                />
                <div className="mt-2">
                  <Badge count={filteredProfiles.length} color="blue" />
                  <Text type="secondary" className="ml-2">profiles found</Text>
                </div>
              </Col>
              <Col span={12}>
                <Text strong>Select Profile for Event Comparison:</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Search for a profile"
                  loading={profilesLoading || searchLoading}
                  showSearch
                  onSearch={handleProfileSearch}
                  onChange={handleProfileSelect}
                  value={selectedProfile?._id}
                  filterOption={false}
                  allowClear
                  onClear={handleClearSelection}
                  options={searchResults.map(profile => ({
                    value: profile._id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={profile.picture} 
                          size={24} 
                          style={{ marginRight: 8 }}
                        />
                        <span>{profile.username}</span>
                      </div>
                    ),
                  }))}
                />
              </Col>
            </Row>
            
            {/* Profile Results Display */}
            {filteredProfiles.length > 0 && searchValue && (
              <div className="mt-4">
                <Text type="secondary" className="text-xs">Click on a profile to view their events:</Text>
                <div className="max-h-64 overflow-y-auto border rounded p-2 bg-gray-50 mt-2">
                  <Row gutter={[8, 8]}>
                    {filteredProfiles.slice(0, 30).map((profile) => (
                      <Col key={profile._id} span={8}>
                        <Card 
                          size="small" 
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          bodyStyle={{ padding: '8px' }}
                          onClick={() => {
                            handleProfileSelect(profile._id);
                            message.info(`Selected: ${profile.username || profile.profilename}`);
                          }}
                        >
                          <div className="flex items-center">
                            <Avatar 
                              src={profile.picture} 
                              icon={!profile.picture && <UserOutlined />}
                              size={32} 
                              style={{ marginRight: 8, flexShrink: 0 }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {profile.username || profile.profilename || 'Unknown'}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {[
                                  profile.location || profile.city?.name,
                                  profile.country?.name || profile.country
                                ].filter(Boolean).join(', ') || 'No location'}
                              </div>
                              {profile.profileType && (
                                <div className="text-xs text-blue-500">
                                  {profile.profileType === 'M' ? 'Artist' : profile.profileType === 'O' ? 'Organizer' : 'Profile'}
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  {filteredProfiles.length > 30 && (
                    <div className="text-center mt-2 text-gray-500 text-sm">
                      Showing first 30 results. Refine your search for more specific results.
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {searchValue && filteredProfiles.length === 0 && (
              <div className="mt-4 text-center text-gray-500">
                No profiles found matching "{searchValue}"
              </div>
            )}
          </Card>
        </Col>
        
        <Col span={24}>
          <div className="flex justify-end">
            {selectedProfile && (
              <Button 
                icon={<CloseCircleOutlined />} 
                onClick={handleClearSelection}
                type="text"
              >
                Clear Selection
              </Button>
            )}
          </div>
        </Col>

        {selectedProfile && (
          <Col span={24}>
            <Card size="small" className="mb-4">
              <div className="flex items-center justify-between">
                <Space>
                  <Avatar src={selectedProfile.picture} size={40} />
                  <div>
                    <Text strong className="text-lg">{selectedProfile.username}</Text>
                    <br />
                    <Space>
                      <Text type="secondary">{selectedProfile.email}</Text>
                      <Text type="secondary">•</Text>
                      <Text type="secondary">{selectedProfile.city?.name}, {selectedProfile.country?.name}</Text>
                    </Space>
                    <br />
                    <Space>
                      <Badge count={events.length} color="green" />
                      <Text type="success">
                        <CalendarOutlined /> upcoming events
                      </Text>
                    </Space>
                  </div>
                </Space>
                {selectedProfile.bio && (
                  <div className="max-w-md">
                    <Text className="text-sm text-gray-600" ellipsis={{ rows: 2 }}>
                      {selectedProfile.bio}
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        )}

        <Col span={24}>
          {eventsLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '16px' }}>Loading events...</p>
            </div>
          )}

          {!eventsLoading && selectedProfile && events.length === 0 && (
            <Empty 
              description={`No events found for ${selectedProfile.username}`}
              style={{ padding: '40px' }}
            />
          )}

          {!eventsLoading && !selectedProfile && (
            <Empty 
              description="Select a profile to view their events"
              style={{ padding: '40px' }}
            />
          )}

          {!eventsLoading && events.length > 0 && (
            <Row gutter={[16, 16]}>
              {sortedEvents.map((event, index) => (
                <Col key={event._id || index} xs={24} sm={12} lg={8} xl={6}>
                  <EventCard 
                    event={event}
                    style={{
                      borderLeft: '4px solid #52c41a' // Green border for all upcoming events
                    }}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default ProfileEventsViewer;